import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import {
	DataTable,
	PageHeader,
	SearchBar,
	Status,
	ContentMessage,
	Badge
} from 'axway-react-ui';
import { Button, Glyphicon } from 'react-bootstrap';
import Fuse from 'fuse.js';
import browserHistory from 'components/utility/history';

// not exposed by this version of fuse once you overwrite it so get a clean one
const { getFn } = new Fuse([], {}).options;

function formatEditFlowLink(flow) {
	return `${window.$conf.prefix}/project/flows/${flow}/edit`;
}

function formatCreateFlowLink(friendlyName) {
	return `${window.$conf.prefix}/project/flows/create?friendlyName=${encodeURIComponent(friendlyName)}`;
}

class TriggersListView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.onSearch = this.onSearch.bind(this);
		this.applySearchFilter = this.applySearchFilter.bind(this);
		this.state = {};

		// distance x threshold = no. of chars it will search within (starting from the left)
		this.fuseOpts = {
			threshold: 0.1,
			distance: 1000,
			keys: [
				'name',
				'description',
				'summary',
				'labels'
			],
			getFn(obj, path) {
				// allow the user to search for 'none' and return the flows
				// triggered by 'none'. Use the original getFn search after tweaking
				// the labels to include the search term we want.
				if (path === 'labels' && !obj.labels.length) {
					// don't modify the original object as this will modify what the
					// filter returns.
					return getFn({
						...obj,
						labels: [ 'none' ]
					}, path);
				}
				return getFn(obj, path);
			}
		};
		this.fuse = new Fuse(props.triggers, this.fuseOpts);
	}

	viewFlow(ev, flow) {
		ev.stopPropagation();
		ev.preventDefault();
		browserHistory.push(formatEditFlowLink(flow));
	}

	onSearch(filter) {
		let searchFilter;
		if (filter) {
			searchFilter = filter;
		}
		this.setState({
			searchFilter
		});
	}

	applySearchFilter(items) {
		if (this.state.searchFilter && items) {
			return this.fuse.search(this.state.searchFilter);
		}
		return items;
	}

	componentWillReceiveProps(props) {
		this.fuse = new Fuse(props.triggers, this.fuseOpts);
	}

	renderTable(triggers) {
		const columns = [
			{
				key: 'name',
				name: this.intl.formatMsg('Flow name'),
				className: 'name',
				format: item => {
					if (!item.name) {
						return (
							<span className="missing-flow">
								{this.intl.formatMsg('Missing flow')}
							</span>
						);
					}
					return (
						<a
							href={formatEditFlowLink(item.flowId)}
							onClick={(ev) => this.viewFlow(ev, item.flowId)}
						>
							{item.name}
						</a>
					);
				}
			},
			{
				key: 'description',
				name: this.intl.formatMsg('Description'),
				className: 'description'
			},
			{
				key: 'trigger-types',
				name: this.intl.formatMsg('Triggered by'),
				className: 'trigger-types',
				format: item => {
					if (!item.labels || !item.labels.length) {
						return (
							<div>
								<Badge bsStyle="neutral">none</Badge>
							</div>
						);
					}
					return (
						<div>
							{item.labels.map((type) => {
								return <Badge key={type} bsStyle="info">{type}</Badge>;
							})}
						</div>
					);
				}
			},
			{
				key: 'status',
				name: this.intl.formatMsg('Status'),
				className: 'status',
				format: item => {
					if (item.status === 'enabled') {
						return (
							<Status type="enabled" />
						);
					} else {
						return (
							<Status type="error" />
						);
					}
				}
			}
		];

		return (
			<DataTable
				columns={columns}
				items={triggers}
			/>
		);
	}

	renderEmpty() {
		const { searchFilter } = this.state;
		// The default no flows message
		const noFlows = this.intl.formatMsg('No flows found.');
		// No flows matched the resulted filter options
		const noFilteredResults = this.intl.formatMsg(
			'No flows matched \'{search}\'.', { search: searchFilter });
		return (
			<ContentMessage
				detail={searchFilter ? noFilteredResults : noFlows}
				imgClass="gg icon-GG-empty-list"
				className="sm empty"
				inlineContent
			/>
		);
	}

	createFlow(ev) {
		ev.stopPropagation();
		ev.preventDefault();
		const triggers = this.props.triggers;
		let exists = true;
		let counter = 0;
		let friendlyName = 'New Flow';
		do {
			let found = false;
			for (const trigger of triggers) {
				if (trigger.name === friendlyName) {
					counter++;
					if (friendlyName !== 'New Flow') {
						// This strips the counter from the friendly name
						const strippedName = friendlyName.split(' ').slice(0, -1).join(' ');
						friendlyName = `${strippedName} ${counter}`;
					} else {
						friendlyName = `${friendlyName} ${counter}`;
					}
					found = true;
					break;
				}
			}
			exists = found;
		} while (exists);
		browserHistory.push(formatCreateFlowLink(friendlyName));
	}

	render() {
		const { triggers: items } = this.props;
		const triggers = this.applySearchFilter(items);
		return (
			<div className="triggers-list-view">
				<PageHeader>
					<PageHeader.Section align="left">
						<PageHeader.Title
							infoPopover={{
								id: 'triggers-info',
								description: this.intl.formatMsg('Flows can be used to implement APIs and orchestrate integrations between different services. They can be invoked by a variety of flow-triggers.'),
								docLink: window.$conf.docFlowUrl
							}}
						>
							{this.intl.formatMsg('Flows')}
						</PageHeader.Title>
					</PageHeader.Section>
					<PageHeader.Section>
						<SearchBar
							onChange={this.onSearch}
						/>
					</PageHeader.Section>
					<PageHeader.Section align="right">
						<Button
							bsSize="large"
							bsStyle="primary"
							className="compact"
							onClick={ev => this.createFlow(ev)}
						>
							<Glyphicon glyph=" gg icon-GG-plus" />{this.intl.formatMsg('Flow')}
						</Button>
					</PageHeader.Section>
				</PageHeader>
				{triggers.length
					? this.renderTable(triggers)
					: this.renderEmpty()
				}
			</div>
		);
	}
}

TriggersListView.propTypes = {
	triggers: PropTypes.array.isRequired
};

export default injectIntl(TriggersListView);
